import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../config/roles';
import logo from '../assets/logo.png';

// Public landing page — visible to everyone, authenticated or not.
// Authenticated users are redirected to their dashboard automatically.

const STEPS = [
  {
    title: 'Talk with our team',
    body: 'Tell us you are interested in care. We can explain our services, answer general questions, and help you understand what happens next.',
  },
  {
    title: 'Check fit and coverage',
    body: 'Our team will help you explore therapist availability and whether your insurance may cover services before you begin.',
  },
  {
    title: 'Create and verify your account',
    body: "When you are ready, create your secure portal account. We'll email you a verification link before you can sign in.",
  },
  {
    title: 'Complete your next steps',
    body: 'Sign in to review informed consent and complete your guided intake. Our team will stay connected as you begin care.',
  },
];

const Home = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // If already logged in, send to the right place
  React.useEffect(() => {
    if (!currentUser) return;
    switch (currentUser.role) {
      case ROLES.CLIENT:    navigate('/client-interface', { replace: true }); break;
      case ROLES.BILLING:   navigate('/billing',     { replace: true }); break;
      case ROLES.THERAPIST: navigate('/my-clients',  { replace: true }); break;
      case ROLES.ADMIN:     navigate('/my-clients',  { replace: true }); break;
      default:              navigate('/client-interface', { replace: true }); break;
    }
  }, [currentUser, navigate]);

  // Still show the page while the redirect fires (avoids flash)
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f6f3ec' }}>

      {/* ── Dark hero band: nav + logo ───────────────────────────── */}
      <div style={{ backgroundColor: '#0f3019' }}>
        <nav className="flex items-center justify-end px-8 py-5">
          <Link
            to="/login"
            className="text-sm font-medium px-4 py-2 rounded-lg border border-white text-white hover:bg-white hover:text-green-900 transition-colors"
          >
            Sign In
          </Link>
        </nav>
        <div className="text-center px-6 pb-14 pt-2">
          <img
            src={logo}
            alt="Integrity Mental Health &amp; Coaching"
            className="mx-auto h-24 w-auto object-contain"
          />
        </div>
      </div>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-16 text-center">
        <h1 className="text-4xl font-extrabold leading-tight mb-5"
            style={{ color: '#1e562d' }}>
          Where Human Wellness Meets<br />the Power of Technology
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed">
          At Integrity Mental Health &amp; Coaching, we stand at the intersection of
          compassionate care and thoughtful technology — helping you understand yourself
          more deeply and move forward with confidence. This is your client portal —
          the place to sign up, complete your intake, and stay connected with your therapist
          between sessions.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/client-signup"
            className="px-8 py-4 rounded-xl text-white font-semibold text-base shadow-md hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#1e562d' }}
          >
            New Client? Get Started →
          </Link>
          <Link
            to="/login"
            className="px-8 py-4 rounded-xl font-semibold text-base border-2 hover:bg-white transition-colors"
            style={{ borderColor: '#1e562d', color: '#1e562d' }}
          >
            Sign In to Your Account
          </Link>
        </div>
      </section>

      {/* ── New client orientation ─────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="rounded-2xl border border-brand-100 bg-white p-6 sm:p-8 shadow-sm">
          <div className="grid gap-6 md:grid-cols-3">
            <div>
              <div className="text-sm font-semibold text-brand-700">Talk to a real person</div>
              <p className="mt-2 text-sm leading-relaxed text-gray-500">
                Start with a conversation. Our team can answer general questions and help you decide whether to take the next step.
              </p>
            </div>
            <div>
              <div className="text-sm font-semibold text-brand-700">Explore insurance coverage</div>
              <p className="mt-2 text-sm leading-relaxed text-gray-500">
                Share your plan details securely after connecting with us, and we will help you understand available coverage options.
              </p>
            </div>
            <div>
              <div className="text-sm font-semibold text-brand-700">Receive clear guidance</div>
              <p className="mt-2 text-sm leading-relaxed text-gray-500">
                We will explain our approach, therapist availability, and the account and intake steps without rushing you.
              </p>
            </div>
          </div>
          <div className="mt-7 flex flex-col items-start justify-between gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:items-center">
            <p className="text-sm text-gray-600">New here? You can speak with our team before creating an account.</p>
            <a
              href="mailto:shane@shanebruce.com?subject=New%20client%20inquiry"
              className="inline-flex rounded-lg border border-brand-700 px-4 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand-300"
            >
              Contact Our Team
            </a>
          </div>
          <p className="mt-3 text-xs text-gray-400">Please do not include private health details in email.</p>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────── */}
      <section className="max-w-3xl mx-auto px-6 pb-20">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#93702b' }}>
            Getting started
          </p>
          <h2 className="text-2xl font-bold" style={{ color: '#1e562d' }}>
            Your First Steps, One at a Time
          </h2>
        </div>

        <div className="space-y-4">
          {STEPS.map(({ title, body }, i) => (
            <div key={title}
                 className="flex items-start gap-5 bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
                   style={{ backgroundColor: '#1e562d' }}>
                {i + 1}
              </div>
              <div>
                <h3 className="font-semibold text-base mb-1" style={{ color: '#434344' }}>
                  {title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Privacy note ─────────────────────────────────────────── */}
      <section className="text-center pb-16 px-6">
        <p className="text-xs text-gray-400 max-w-xl mx-auto">
          Your privacy matters. All personal information is stored securely and protected under HIPAA.
          Your data is never shared with AI systems in identifiable form.
          <br />
          <span className="font-medium" style={{ color: '#7d9483' }}>
            Integrity Mental Health &amp; Coaching · Santa Barbara, CA
          </span>
          <br />
          <a href="https://integritymhc.com" target="_blank" rel="noreferrer"
             className="hover:underline" style={{ color: '#7d9483' }}>
            integritymhc.com
          </a>
          <br />
          <span className="inline-block mt-3">
            Need help with the portal? Contact{' '}
            <a href="mailto:shane@shanebruce.com"
               className="hover:underline font-medium" style={{ color: '#7d9483' }}>
              shane@shanebruce.com
            </a>
          </span>
        </p>
      </section>

    </div>
  );
};

export default Home;
